// Message-Requests API client — the cold-contact gate ("Requests" tab) + the
// thread-level timer deactivation (D-011).
//
// Isolated from the shared services/api.js on purpose (small PR surface, no risk
// to the shared axios instance) — mirrors services/clubNetworkApi.js and
// services/agentTenderApi.js. Same {success, data, error, status} return shape as
// chatActionService so the integrator can consume it identically.
//
// ROUTE ASSUMPTIONS (P2-BE-1 builds the extended viewset in parallel — D-011):
//   The MessageRequest viewset is registered today at
//   `dashboard/last-minute/message-requests/` (actions: create, received, sent,
//   accept, ignore). The canonical "Requests" gate extends THAT viewset in place
//   (the model docstring says the design "re-skins and extends it without
//   replacing it"), so the prefix is kept. If P2-BE-1 relocates the viewset,
//   change PREFIX below — nothing else moves.
//   - cooldown-status/ and the club-side counts/ actions are additive (D-011);
//     both feature-detect (404 -> caller degrades gracefully).
//   - deactivateTimer POSTs to `dashboard/chat/deactivate-timer/` {channel_cid}
//     (D-011 (1), controller-only, mirrors extend/revive).
import axios from "axios";

// Vocabulary (intents + origins) lives in an axios-free module so pure consumers
// (chat intent filter, chips, tests) don't drag the HTTP client into their graph.
// Re-exported here unchanged so existing importers of this file keep working.
export {
  MESSAGE_REQUEST_INTENTS,
  MESSAGE_REQUEST_INTENT_BY_KEY,
  MESSAGE_REQUEST_ORIGINS,
  MESSAGE_REQUEST_ORIGIN_BY_KEY,
  MESSAGE_REQUEST_DEFAULT_CAP,
} from "./messageRequestVocab";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

// Single knob for the integrator if the backend prefix changes.
const PREFIX = "/dashboard/last-minute/message-requests";
const DEACTIVATE_TIMER_PATH = "/dashboard/chat/deactivate-timer/";

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

// Same Bearer-token attachment the main client uses (authSlice persists
// localStorage "tokens" = { access, refresh }).
authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token available — request proceeds and the server returns 401 */
  }
  return config;
});

const ok = (response, extra = {}) => ({
  success: true,
  data: response?.data,
  status: response?.status,
  ...extra,
});

const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    "Something went wrong. Please try again.",
});

const messageRequestService = {
  // Received (recipient inbox) — the "Requests" gate. PENDING-only (accepted
  // graduate to chats, ignored vanish — confirmed P2-BE-1). Optional intent/origin
  // filters map to querystring params; the server also returns per-intent `counts`
  // and per-origin `origin_counts`, and stamps each row with `is_older` (>30d).
  listReceived: async ({ intent, origin } = {}) => {
    try {
      const params = {};
      if (intent && intent !== "all") params.intent = intent;
      if (origin && origin !== "all") params.origin = origin;
      const response = await authApi.get(`${PREFIX}/received/`, { params });
      return ok(response, {
        results: response.data?.results || [],
        counts: response.data?.counts || null,
        origin_counts: response.data?.origin_counts || null,
      });
    } catch (error) {
      return fail(error);
    }
  },

  // Sent (sender side) — only ever pending/accepted (Invariant 9). The backend
  // excludes withdrawn/expired rows, so this list IS the sender's open outbox:
  // its pending count equals cooldownStatus().open_count — the N behind design
  // frame 1r's "View your N pending requests".
  //
  // Each row carries a `recipient` identity block {name, agency, agent_id,
  // profile_picture_url} (D18 person-first) plus the flat `recipient_agent_id`
  // the profile/search "Request pending" chip matches on.
  listSent: async () => {
    try {
      const response = await authApi.get(`${PREFIX}/sent/`);
      return ok(response, { results: response.data?.results || [] });
    } catch (error) {
      return fail(error);
    }
  },

  // Withdraw -> the SENDER takes their own still-pending request back (D12).
  // Frees a cap slot and, like ignore, is quiet: the recipient is not notified,
  // the request simply leaves their Requests tab. Withdraw is NOT a rejection
  // signal — it is the sender's own action, so it does not violate Invariant 9.
  //
  // Idempotent (a second call on an already-terminal row is a harmless no-op).
  // 409 = the request was accepted in the meantime; a chat already exists, so
  // the caller must refresh the row into its accepted/chat state rather than
  // remove it. Sender-scoped: someone else's request 404s.
  withdrawRequest: async (id) => {
    try {
      const response = await authApi.post(`${PREFIX}/${id}/withdraw/`);
      return ok(response);
    } catch (error) {
      return fail(error);
    }
  },

  // Per-intent counts for the filter chips (design 1f). Additive backend action;
  // if it isn't live yet the caller derives counts from listReceived() instead.
  counts: async ({ origin } = {}) => {
    try {
      const params = {};
      if (origin && origin !== "all") params.origin = origin;
      const response = await authApi.get(`${PREFIX}/counts/`, { params });
      return ok(response);
    } catch (error) {
      return fail(error);
    }
  },

  // Cold-contact compose (design 1i/1j). Recipient is `recipient_agent_id` = the
  // Agent row PK (NOT Account/UserProfile id — confirmed P2-BE-1). intent + first
  // message; intent_manual_text carries the free-text when intent === 'manual'.
  // origin is SYSTEM-SET server-side: pass listing_id → server forces
  // last_minute; otherwise the optional `origin` hint is clamped to
  // agent_requests|direct (default direct). On the 24h cap the server returns
  // 429 with {blocked, blocked_until, open_count, cap} — surfaced on the result
  // so the caller can flip to the 1r capped banner without a second round-trip.
  //
  // TWO success shapes (P2-BE-1): cross-org → 201 + sender payload (no extra
  // fields below are set). SAME-ORG colleagues → 200 {same_org:true, channel_cid,
  // channel_type} with NO request row (they chat freely, Marin's rule); those
  // three fields are surfaced flat so the composer can open the chat instead of
  // showing "Request sent". On 201 they are undefined/empty → same_org path off.
  createRequest: async ({
    recipient_agent_id,
    intent,
    intent_manual_text,
    message,
    listing_id,
    origin,
  }) => {
    try {
      const payload = { recipient_agent_id, intent, message };
      if (intent === "manual" && intent_manual_text) {
        payload.intent_manual_text = intent_manual_text;
      }
      if (listing_id) payload.listing_id = listing_id;
      if (origin) payload.origin = origin;
      const response = await authApi.post(`${PREFIX}/`, payload);
      return ok(response, {
        same_org: response.data?.same_org === true,
        channel_cid: response.data?.channel_cid || "",
        channel_type: response.data?.channel_type || "messaging",
      });
    } catch (error) {
      const result = fail(error);
      // Cap hit: pass the cooldown fields through so the composer can render 1r.
      if (error?.response?.status === 429) {
        const body = error.response.data || {};
        result.cooldown = {
          blocked: body.blocked ?? true,
          blocked_until: body.blocked_until ?? null,
          open_count: body.open_count,
          cap: body.cap,
        };
      }
      return result;
    }
  },

  // Accept -> opens the 14-day agent↔agent chat (accepter controls the timer).
  // Returns channel_cid so the caller can deep-link "Open thread".
  acceptRequest: async (id) => {
    try {
      const response = await authApi.post(`${PREFIX}/${id}/accept/`);
      return ok(response, {
        channel_cid: response.data?.channel_cid || "",
      });
    } catch (error) {
      return fail(error);
    }
  },

  // Ignore -> SILENT removal (sender never notified; Invariant 9). Idempotent.
  ignoreRequest: async (id) => {
    try {
      const response = await authApi.post(`${PREFIX}/${id}/ignore/`);
      return ok(response);
    } catch (error) {
      return fail(error);
    }
  },

  // Composer cap/cooldown state (design 1r). {open_count, cap, blocked,
  // blocked_until}. Feature-detects (404 -> caller treats as not-blocked).
  cooldownStatus: async () => {
    try {
      const response = await authApi.get(`${PREFIX}/cooldown-status/`);
      return ok(response);
    } catch (error) {
      return fail(error);
    }
  },

  // Thread-level timer deactivation (D-011 (1)); controller-only, keyed by cid.
  deactivateTimer: async (channel_cid) => {
    try {
      const response = await authApi.post(DEACTIVATE_TIMER_PATH, {
        channel_cid,
      });
      return ok(response);
    } catch (error) {
      return fail(error);
    }
  },
};

export default messageRequestService;
